import { object, string } from 'yup';
import { JsonObject } from '@/lib/helpers';
import { BaseModel, CreateBaseModel } from '@/lib/models/base.model';
import { PrivateModelConfig, PrivateModelProvider } from '.';

export class PrivateModelModel extends BaseModel {
  orgId: string;
  name: string;
  scope: string;
  connectorId: string;
  provider: PrivateModelProvider;
  modelId: string; // <connector type>.<connector service>.<model identifier>
  config: PrivateModelConfig;
  status: string;
  statusMessage: string;
  lastInferenceTestAt: string;

  schema = object({
    name: string().required('Name is required'),
    connectorId: string().required('Please choose a connector'),
    modelId: string().required('Please choose a model'),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof PrivateModelModel, unknown>;

    this.orgId = (data.orgId as string) || '';
    this.name = (data.name as string) || '';
    this.scope = (data.scope as string) || 'org';
    this.connectorId = (data.connectorId as string) || '';
    this.provider = (data.provider as PrivateModelProvider) || '';
    this.modelId = (data.modelId as string) || '';
    this.status = (data.status as string) || '';
    this.statusMessage = (data.statusMessage as string) || '';
    this.lastInferenceTestAt = (data.lastInferenceTestAt as string) || '';
    this.config = (data.config as PrivateModelConfig) || {};
  }

  // extending model-type-specific classes should override this as-needed
  get typedProps(): JsonObject {
    return {};
  }

  get niceStatusMessage(): string {
    return this.statusMessage.replace(/^litellm\./, '');
  }

  get niceModelId(): string {
    return this.modelId.replace(/-\d{8}-.*$/, ''); // Match example `us.anthropic.claude-3-5-sonnet-20260306-v1.0`
  }
}

export class CreatePrivateModelModel extends CreateBaseModel {
  name: string;
  connectorId: string;
  provider: string;
  modelId: string;
  config: PrivateModelConfig;
  typedProps: JsonObject;

  constructor(privateModel: PrivateModelModel) {
    super();

    this.name = privateModel.name;
    this.connectorId = privateModel.connectorId;
    this.provider = privateModel.provider;
    this.modelId = privateModel.modelId;
    this.config = privateModel.config;
    this.typedProps = privateModel.typedProps;
  }

  get _props(): Record<string, unknown> {
    const props = {
      name: this.name,
      connectorId: this.connectorId,
      provider: this.provider,
      modelId: this.modelId,
      config: this.config,
      ...this.typedProps,
    };

    return props;
  }
}
